import { useCallback, useEffect, useRef, useState } from 'react'

export interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
  /** Re-runs the loader, e.g. after a mutation. */
  reload: () => void
  /** Replaces the data locally, for optimistic updates. */
  setData: (updater: (current: T | null) => T | null) => void
}

/**
 * Minimal data-loading hook.
 *
 * The app has a handful of screens and no shared server cache to speak of, so a
 * query library would be more machinery than the problem needs. This covers the
 * three things every screen here does want: a loading flag, an error string,
 * and a way to reload after a mutation — plus a guard against setting state
 * from a request whose component has already unmounted.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setDataState] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  // Keeps the latest loader without making it a dependency, so callers can pass
  // an inline arrow function without re-running on every render.
  const loaderRef = useRef(loader)
  loaderRef.current = loader

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    loaderRef
      .current()
      .then((result) => {
        if (!cancelled) setDataState(result)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Something went wrong')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // The caller owns the dependency list; `nonce` is how reload() re-triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => {
    setNonce((n) => n + 1)
  }, [])

  const setData = useCallback((updater: (current: T | null) => T | null) => {
    setDataState((current) => updater(current))
  }, [])

  return { data, loading, error, reload, setData }
}
